<template>
  <form>
    <div class="row mb-2">
      <div class="col-sm-12">

        <div class="row mt-2">
          <label for="name" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.name") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`name`)">
              <input :class="parentProps.class" v-model="form.name" type="text" class="form-control " id="name">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label for="email" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.email") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`email`)">
              <input v-model="form.email" :class="parentProps.class" :readonly="!!form.id" type="text" class="form-control " id="email">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label for="appId" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.domain") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`appId`)">
              <Multiselect id="referralId"
                           class=""
                           :class="parentProps.class"
                           v-model="form.appId"
                           :options="store.getters['getAppsIds']"
                           :custom-label="(opt) => store.getters['getAppById'](opt)?.domain"
                           select-label="Select"
                           placeholder=""
                           open-direction="bottom"
                           :multiple="false"
                           :searchable="true"
                           :internal-search="true"
                           :clear-on-select="false"
                           :close-on-select="true"
                           :options-limit="100"
                           :limit="1000"
                           :show-no-results="false"
                           :hide-selected="false"
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label for="is_blocked" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.is_blocked") }}</label>
          <div class="col-sm-8">
            <div class="form-check form-switch d-flex align-items-center">
              <input  v-model="form.isBlocked" class="form-check-input" type="checkbox" id="is_blocked">
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <label for="isManualWithdrawals" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.isManualWithdrawals") }}</label>
          <div class="col-sm-8">
            <div class="form-check form-switch d-flex align-items-center">
              <input  v-model="form.isManualWithdrawals" class="form-check-input" type="checkbox" id="isManualWithdrawals">
            </div>
          </div>
        </div>

        <div class="row mt-2" v-if="!form.id">
          <label for="createInvite" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.create_invite") }}</label>
          <div class="col-sm-8">
            <div class="form-check form-switch d-flex align-items-center">
              <input  v-model="form.createInvite" class="form-check-input" type="checkbox" id="createInvite">
            </div>
          </div>
        </div>

        <div class="row mt-2" v-if="(form.id || form.createInvite === false) && store.getters.hasPermission(passwordPermission)">
          <label for="password" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.password") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`password`)" >
              <div class="input-group" :class="parentProps.class">
                <input v-model="form.password" :class="parentProps.class" type="text" class="form-control " id="password">
                <button @click.prevent="form.password = helper.generatePassword()" type="button" class="btn btn-outline-secondary"><i class="fa fa-rotate"></i></button>
              </div>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2" v-if="role === UserRole.OPERATOR || (role === UserRole.ADMIN || role === UserRole.TRADER_TEAM_LEAD) && store.getters.hasPermission(UserPermission.EDIT_PERMISSIONS)">
          <label for="permissions" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.permissions") }}</label>
          <div class="col-sm-8">
            <div class="" v-for="permission in userPermissionsFiltered">
              <div class="form-check small mb-0">
                <input class="form-check-input" :value="permission.id" v-model="form.permissions" type="checkbox" :id="permission.id">
                <label class="form-check-label" :for="permission.id">
                  {{ permission.name}}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2" v-if="form.id && (role === UserRole.REFERRAL || role === UserRole.TRADER_TEAM_LEAD)">
          <label for="2fa" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.2fa") }}</label>
          <div class="col-sm-8 align-items-center d-flex">
            <span v-if="form?.is2faEnabled" class="text-success">{{ helper.trans('labels.enabled')}}</span>
            <span v-if="!form?.is2faEnabled" class="text-warning"><i class="fa fa-warning"></i> {{ helper.trans('labels.disabled')}}</span>
            <button v-if="form?.is2faEnabled && store.getters.hasPermission(UserPermission.DISABLE_USER_2FA)" type="button" class="btn btn-danger ms-2 btn-sm text-capitalize-first" @click.prevent="disable2fa(form.id)">{{ helper.trans("labels.disable")}}</button>
          </div>
        </div>

      </div>
    </div>
    <ConfirmModal ref="twoFaConfirmModal"></ConfirmModal>
  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, computed, PropType, Prop, onMounted, ref} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import Multiselect from "vue-multiselect";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import UserFormDTO from "@/models/form/UserFormDTO";
import UserUpdateRequestDTO from "@/models/request/UserUpdateRequestDTO";
import UserCreateRequestDTO from "@/models/request/UserCreateRequestDTO";
import {UserRole} from "@/models/enum/UserRole";
import UserDTO from "@/models/UserDTO";
import PaginationDTO from "@/models/PaginationDTO";
import {UserPermission} from "@/models/enum/UserPermission";
import UserPermissionDTO from "@/models/UserPermissionDTO";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";

const components = {
  Multiselect,
  FormInputWrapper,
  ConfirmModal
}

const api = new APIClient();
const notification = useNotification()

export default defineComponent({
  props: {
    role: String
  },
  components,
  setup(props) {
    const twoFaConfirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null)
    const store = useStore();
    const state = reactive({
      teamLeads: [] as UserDTO[],
      isTeamLeadsSelectLoading: false,
      errors: {} as ValidationErrorDTO[]|any,
      form: {} as UserFormDTO,
      userPermissions: [] as UserPermissionDTO[]
    });

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const applySavedUser = (user: UserDTO) => {
      if (!user?.id) {
        return
      }

      state.form = {
        ...state.form,
        ...user,
        password: '',
        permissions: Array.isArray(user.permissions) ? [...user.permissions] : [],
        role: props.role ?? user.role,
      }
    }

    const save = (): Promise<any> => {
      setErrors([]);
      const form = getForm();

      let promise;
      if (form.id) {
        const passwordPromise = form.password
          ? api.usersChangePassword(form.id, form.password)
          : Promise.resolve();

        if (store.getters.isOperator) {
          promise = passwordPromise.then(() => form as unknown as UserDTO);
        } else {
          const data = {
            ...form,
            password: null,
          } as UserUpdateRequestDTO;

          promise = api.usersUpdate(form.id, data).then(async (resp) => {
            await passwordPromise;
            applySavedUser(resp);
            await loadPermissions();
            return resp;
          });
        }

        promise = promise.then((resp) => {
          notification.notify({title: helper.trans('users.notice.updated')});

          return resp;
        })
      } else {
        const data = {
          ...form,
          password: form.createInvite ? null : (form.password ? form.password : null),
          createInvite: form.createInvite,
        } as UserCreateRequestDTO;

        promise = api.usersCreate(data)
          .then(async (resp) => {
            notification.notify({title: helper.trans('users.notice.created')});
            applySavedUser(resp);
            await loadPermissions();

            return resp;
          })
      }

      promise
        .catch((e: any) => {
          if (e.response && e.request && e.request.status === 400 && e.response.data) {
            setErrors(e.response.data)
          } else {
            console.log(e);
          }

        })

      return promise
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const hasErrors = (path: string) => {
      return state.errors[path]?.length > 0
    }

    const getForm = (): UserFormDTO => {
      return state.form
    }

    const setForm = (form: UserFormDTO) => {
      asyncFindTeamLeads('')
      loadPermissions()
      state.errors = {}
      state.form = {
        ...form,
        permissions: Array.isArray(form.permissions) ? [...form.permissions] : [],
      }
    }

    const loadPermissions = async () => {
      const isAdminOrTeamLead = props.role === UserRole.ADMIN || props.role === UserRole.TRADER_TEAM_LEAD;
      if (props.role !== UserRole.OPERATOR && (!store.getters.hasPermission(UserPermission.EDIT_PERMISSIONS) || !isAdminOrTeamLead)) {
        state.userPermissions = [];
        return;
      }

      state.userPermissions = await api.call.userPermissions.list()
    }

    const asyncFindTeamLeads = async (query: string) => {
      state.isTeamLeadsSelectLoading = true;
      state.teamLeads = (await api.users({limit: 100, offset: 0} as PaginationDTO, {query: query, roles: [UserRole.TRADER_TEAM_LEAD]})).items;
      state.isTeamLeadsSelectLoading = false
    }

    onMounted(() => {
      store.dispatch('loadApps');
      loadPermissions();
    })

    const userPermissionsFiltered = computed(() => {
      return state.userPermissions.filter(item => item.roles.includes(props.role!))
    })

    const passwordPermission = computed(() => {
      switch (props.role) {
        case UserRole.ADMIN: return UserPermission.RESET_ADMIN_PASSWORD;
        case UserRole.OPERATOR: return UserPermission.RESET_OPERATOR_PASSWORD;
        default: return UserPermission.RESET_TRADER_PASSWORD; // REFERRAL, TRADER_TEAM_LEAD
      }
    })

    const disable2fa = async (id: number) => {
      twoFaConfirmModal.value?.show({
        title: helper.trans('labels.2fa_disable_confirmation'),
        okTitle: null,
        cancelTitle: null,
        onOkClick: async () => {
          try {
            await api.user2faDisable(id);
            notification.notify({title: helper.trans('labels.2fa_disabled')});
            state.form.is2faEnabled = false
          } finally {
          }
        }
      } as ConfirmModalProps);
    }

    return {
      ...toRefs(state),
      getForm,
      setForm,
      store,
      helper,
      numberFormatter,
      setErrors,
      getErrors,
      hasErrors,
      userPermissionsFiltered,
      passwordPermission,
      save,
      UserRole,
      asyncFindTeamLeads,
      UserPermission,
      disable2fa,
      twoFaConfirmModal
    }
  }
})
</script>
